/* eslint-disable default-case */
import React, { useState, useRef, useEffect } from "react";

export const SearchableSelect = ({
  value,
  onChange,
  options,
  name,
  id,
  className,
  disabled,
}) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef(null);


  const filtered = options.filter((opt) =>
    opt.name.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // resetea el índice resaltado cuando cambia el texto de búsqueda
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  // hace scroll automático al item resaltado cuando cambia el índice
  useEffect(() => {
    if (!listRef.current || highlightedIndex < 0) return;
    const items = listRef.current.querySelectorAll("li");
    if (items[highlightedIndex]) {
      items[highlightedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  const handleSelect = (optName) => {
    onChange({ target: { name, value: optName } });
    setIsOpen(false);
    setSearch("");
  };

  // manejo la navegación por teclado
  const handleKeyDown = (e) => {
  if (!isOpen) {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      setIsOpen(true);
    }
    return;
  }

  switch (e.key) {
    // bajo al siguiente item, si llega al final vuelve al primero
    case "ArrowDown":
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
      break;
    // subo al item anterior, si llega al primero va al último
    case "ArrowUp":
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
      break;
    // selecciono el item resaltado  
    case "Enter":
      e.preventDefault();
      if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
        handleSelect(filtered[highlightedIndex].name);
      }
      break;
    // si hay item resaltado lo selecciono, sino cierra y sigue el flujo del formulario
    case "Tab":
      if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
        e.preventDefault();
        handleSelect(filtered[highlightedIndex].name);
      } else {
        setIsOpen(false);
        setSearch("");
        setHighlightedIndex(-1);
      }
      break;
    case "Escape":
      setIsOpen(false);
      setSearch("");
      setHighlightedIndex(-1);
      break;
    }
  };

  const selectedLabel = options.find((opt) => opt.name === value)?.name || value || "";

  return (
    // onKeyDown para poder capturar el evento de las flechas
    <div ref={containerRef} style={{ position: "relative", width: "30%" }} onKeyDown={handleKeyDown}>
      <div
        onClick={() => {
          if (!disabled) setIsOpen((prev) => !prev);
        }}
        id={id}
        className={className}
        style={{
          border: 0,
          background: "transparent",
          padding: ".3rem 0",
          width: "100%",
          cursor: disabled ? "default" : "pointer",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "6px",
          boxSizing: "border-box",
          boxShadow: disabled ? "none" : "0px 1.5px 0px var(--lblue)",
          fontFamily: "'Open Sans', Arial, Helvetica, sans-serif",
          fontSize: "inherit",
          fontWeight: 600,
          color: "#00195a",
          lineHeight: 1.1,
        }}
        tabIndex={disabled ? -1 : 0}
      >
        <span style={{ flex: 1 }}>
          {selectedLabel || (
            <span style={{ opacity: 0.4, fontWeight: 400 }}>Seleccione ...</span>
          )}
        </span>
        <svg
          width="11"
          height="11"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 4L6 8L10 4"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 1050,
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            flexDirection: "column",
            minWidth: "220px",
            width: "100%",
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..."
            style={{
              padding: "6px 10px",
              border: "none",
              borderBottom: "1px solid #eee",
              outline: "none",
              fontSize: "0.875rem",
              width: "100%",
              boxSizing: "border-box",
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <ul
            ref={listRef}
            style={{
              margin: 0,
              padding: 0,
              listStyle: "none",
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {filtered.length === 0 ? (
              <li
                style={{
                  padding: "8px 12px",
                  color: "#999",
                  fontSize: "0.875rem",
                }}
              >
                Sin resultados
              </li>
            ) : (
              filtered.map((opt, index) => (
                <li
                  key={opt.id}
                  onMouseDown={() => handleSelect(opt.name)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                  style={{
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    backgroundColor:
                      index === highlightedIndex
                        ? "#d0e8ff"
                        : opt.name === value
                          ? "#e3f0ff"
                          : "transparent",
                  }}
                >
                  {opt.name}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};